// Row shapes for the Top Dogs schema. These mirror
// supabase/migrations/0001_init.sql, 0002_email.sql and
// 0003_inquiries_and_client_status.sql — keep them in sync when the
// schema changes.

export interface Instructor {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  created_at: string;
}

export interface ClassType {
  id: string;
  name: string;
  // Hex color used to tint the class on the calendar.
  color: string;
  default_duration_minutes: number | null;
  created_at: string;
}

export type LiabilityStatus = 'none' | 'sent' | 'signed';

export interface Client {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  dog_name: string | null;
  breed: string | null;
  age: number | null;
  notes: string | null;
  liability_status: LiabilityStatus;
  // Path within the private `liability-forms` storage bucket.
  liability_form_path: string | null;
  // Active clients show first and are the only ones offered when
  // enrolling into a class; past clients are hidden but kept on record.
  active: boolean;
  created_at: string;
}

export type Recurrence = 'none' | 'weekly';
export type ClassStatus = 'active' | 'cancelled';

// A class "series": one entry the owner creates. Its individual calendar
// occurrences live in `class_sessions` (generated from the recurrence).
export interface ClassRow {
  id: string;
  class_type_id: string;
  instructor_id: string;
  title: string | null;
  start_at: string;
  duration_minutes: number;
  recurrence: Recurrence;
  // How many weekly sessions to generate (1 for a one-off).
  sessions_count: number;
  // When on, a new block of `sessions_count` weekly sessions is
  // generated automatically once the current block runs out.
  recurring: boolean;
  notes: string | null;
  status: ClassStatus;
  // When on, the class page surfaces a prompt to send the next
  // session's reminder. Reminders are always sent by a human click.
  reminders_enabled: boolean;
  // When the owner last sent a reminder for this class (informational).
  last_reminder_at: string | null;
  created_at: string;
}

export type SessionStatus = 'scheduled' | 'cancelled';

export interface ClassSession {
  id: string;
  class_id: string;
  starts_at: string;
  ends_at: string;
  status: SessionStatus;
  note: string | null;
  created_at: string;
}

export interface Enrollment {
  class_id: string;
  client_id: string;
  created_at: string;
}

export interface Holiday {
  id: string;
  holiday_date: string; // YYYY-MM-DD
  label: string;
  created_at: string;
}

// Options offered in the public inquiry form's "Type of class" dropdown.
export const CLASS_INTEREST_OPTIONS = [
  'Puppy',
  'Rally',
  'Competitive Obedience',
  'Nose Work',
  'Private Lessons',
  'Other',
] as const;
export type ClassInterest = (typeof CLASS_INTEREST_OPTIONS)[number];

export type InquiryStatus = 'new' | 'booked' | 'dismissed';

// A submission from the public "Inquire" form on the splash page.
export interface Inquiry {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  dog_name: string | null;
  dog_breed: string | null;
  // Free text — "2 years", "12 weeks", etc. (not every dog's age fits a
  // whole number of years).
  dog_age: string | null;
  class_interest: string | null;
  comments: string | null;
  status: InquiryStatus;
  notes: string | null;
  created_at: string;
}
